import React, { useState, useEffect } from 'react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Loader2, Mail, AlertCircle } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { useLocation } from 'react-router-dom'
import { useTeamSlug } from '@/context/TeamSlugContext'
import { supabase } from '@/integrations/supabase/client'
import { useTranslation } from '@/hooks/useTranslation'
import { AuthLegalNotice } from '@/components/legal/AuthLegalNotice'

interface PublicAuthModalProps {
	isOpen: boolean
	onClose: () => void
	onSuccess?: () => void
}

const RESEND_COOLDOWN_SEC = 60

export function PublicAuthModal({
	isOpen,
	onClose,
	onSuccess,
}: PublicAuthModalProps) {
	const { t } = useTranslation()
	const { toast } = useToast()
	const location = useLocation()
	const { teamSlug } = useTeamSlug()
	const [isLoading, setIsLoading] = useState(false)
	const [email, setEmail] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [magicLinkSent, setMagicLinkSent] = useState(false)
	const [isResending, setIsResending] = useState(false)
	const [resendCountdown, setResendCountdown] = useState<number | null>(null)

	// Reset state when modal closes
	useEffect(() => {
		if (!isOpen) {
			const timer = setTimeout(() => {
				setMagicLinkSent(false)
				setEmail('')
				setError(null)
				setResendCountdown(null)
			}, 300)

			return () => clearTimeout(timer)
		}
	}, [isOpen])

	// Countdown timer for resend email
	useEffect(() => {
		if (resendCountdown === null) return
		if (resendCountdown === 0) return
		const interval = setInterval(() => {
			setResendCountdown((prev) => (prev && prev > 0 ? prev - 1 : 0))
		}, 1000)
		return () => clearInterval(interval)
	}, [resendCountdown])

	const getRedirectURL = () => {
		// Determine the correct callback URL based on environment
		const baseUrl = window.location.origin
		return `${baseUrl}/auth/callback`
	}

	const handleGoogleSignIn = async () => {

		setIsLoading(true)
		setError(null)

		// Save current location for redirect after auth
		sessionStorage.setItem('auth_return_url', location.pathname)
		// Save team slug if available
		if (teamSlug) {
			sessionStorage.setItem('auth_team_slug', teamSlug)
		}

		try {
			const redirectTo = getRedirectURL()

			const { error } = await supabase.auth.signInWithOAuth({
				provider: 'google',
				options: {
					redirectTo,
				},
			})

			if (error) throw error
		} catch (err: any) {
			console.error('[PublicAuthModal] Google OAuth error:', err)
			setError(err.message || 'Failed to initiate Google sign-in')
			setIsLoading(false)
		}
	}

	const handleMagicLinkSignIn = async (e: React.FormEvent) => {
		e.preventDefault()
		setError(null)


		if (!email || !/\S+@\S+\.\S+/.test(email)) {
			setError(t('publicAuthModal.pleaseEnterValidEmail'))
			return
		}

		// Save current location for redirect after auth
		sessionStorage.setItem('auth_return_url', location.pathname)
		// Save team slug if available
		if (teamSlug) {
			sessionStorage.setItem('auth_team_slug', teamSlug)
		}

		setIsLoading(true)
		try {
			const redirectTo = getRedirectURL()

			const { error } = await supabase.auth.signInWithOtp({
				email,
				options: {
					emailRedirectTo: redirectTo,
				},
			})

			if (error) throw error

			setResendCountdown(RESEND_COOLDOWN_SEC)

			setMagicLinkSent(true)
			toast({
				title: t('publicAuthModal.checkYourInbox'),
				description: t('publicAuthModal.magicLinkSentTo', { email }),
			})
			onSuccess?.()
		} catch (err: any) {
			console.error('[PublicAuthModal] Magic link error:', err)
			setError(err.message || t('publicAuthModal.unexpectedError'))
		} finally {
			setIsLoading(false)
		}
	}

	const handleResendMagicLink = async () => {
		if (!email) return

		setIsResending(true)
		setError(null)
		try {
			const redirectTo = getRedirectURL()

			const { error } = await supabase.auth.signInWithOtp({
				email,
				options: {
					emailRedirectTo: redirectTo,
				},
			})

			if (error) throw error

			toast({
				title: t('publicAuthModal.magicLinkResent'),
				description: t('publicAuthModal.checkInboxAgain'),
			})
			setResendCountdown(RESEND_COOLDOWN_SEC)
		} catch (err: any) {
			console.error('[PublicAuthModal] Resend magic link error:', err)
			setError(err.message || t('publicAuthModal.failedToResend'))
		} finally {
			setIsResending(false)
		}
	}

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-md max-w-[95vw] md:mx-4 p-4 sm:p-6">
				<DialogHeader className="space-y-2 sm:space-y-3">
					<DialogTitle className="text-lg sm:text-xl text-center">
						{t('publicAuthModal.joinTheConversation')}
					</DialogTitle>
					<DialogDescription className="text-center text-sm sm:text-base">
						{t('publicAuthModal.signInOrCreateAccount')}
					</DialogDescription>
				</DialogHeader>

				{error && (
					<Alert variant="destructive" className="my-3 sm:my-4">
						<AlertCircle className="h-4 w-4" />
						<AlertTitle>{t('publicAuthModal.error')}</AlertTitle>
						<AlertDescription className="text-sm">{error}</AlertDescription>
					</Alert>
				)}

				{magicLinkSent ? (
					<div className="py-6 sm:py-8 text-center">
						<Mail className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-green-500 mb-3 sm:mb-4" />
						<h3 className="text-base sm:text-lg font-medium">
							{t('publicAuthModal.magicLinkSent')}
						</h3>
						<p className="text-sm text-muted-foreground mt-2 px-2">
							{t('publicAuthModal.signInLinkSent', { email })}
						</p>
						{resendCountdown !== null && resendCountdown > 0 ? (
							<p className="text-xs mt-3 sm:mt-4 text-muted-foreground">
								{t('publicAuthModal.didntReceiveEmail', { seconds: resendCountdown })}
							</p>
						) : (
							<Button
								variant="link"
								className="mt-3 sm:mt-4 h-auto p-0"
								onClick={handleResendMagicLink}
								disabled={isResending}>
								{isResending && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								{t('publicAuthModal.resendEmail')}
							</Button>
						)}
					</div>
				) : (
					<div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
						<Button
							variant="outline"
							className="w-full h-10 sm:h-11 text-sm sm:text-base"
							onClick={handleGoogleSignIn}
							disabled={isLoading}>
							{isLoading ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<svg
									className="mr-2 h-4 w-4"
									aria-hidden="true"
									focusable="false"
									data-prefix="fab"
									data-icon="google"
									role="img"
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 488 512">
									<path
										fill="currentColor"
										d="M488 261.8C488 403.3 381.5 512 244 512 109.8 512 0 402.2 0 261.8S109.8 11.8 244 11.8c70.3 0 129.8 28.7 173.4 74.2l-68.2 65.8c-20.5-19.5-48.4-31.5-77.9-31.5-62.3 0-113.5 51.6-113.5 114.9s51.2 114.9 113.5 114.9c71.2 0 99.1-50.9 102.3-77.3H244v-93.5h244z"></path>
								</svg>
							)}
							{t('publicAuthModal.continueWithGoogle')}
						</Button>

						<div className="relative">
							<div className="absolute inset-0 flex items-center">
								<Separator />
							</div>
							<div className="relative flex justify-center text-xs uppercase">
								<span className="bg-background px-2 text-muted-foreground">
									{t('publicAuthModal.or')}
								</span>
							</div>
						</div>

						<form
							onSubmit={handleMagicLinkSignIn}
							className="space-y-3 sm:space-y-4">
							<div className="space-y-1.5 sm:space-y-2">
								<Label htmlFor="email" className="text-sm">
									{t('publicAuthModal.emailAddress')}
								</Label>
								<Input
									id="email"
									type="email"
									placeholder={t('publicAuthModal.emailPlaceholder')}
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									disabled={isLoading}
									required
									className="h-10 sm:h-11 text-sm sm:text-base"
								/>
							</div>

							<Button
								type="submit"
								className="w-full h-10 sm:h-11 text-sm sm:text-base"
								disabled={isLoading || !email}>
								{isLoading ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<Mail className="mr-2 h-4 w-4" />
								)}
								{t('publicAuthModal.sendMagicLink')}
							</Button>
						</form>
					</div>
				)}

				<DialogFooter className="pt-2 sm:pt-4">
					<AuthLegalNotice className="w-full text-center" />
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
